import { createSlice } from "@reduxjs/toolkit";
import { generateId } from "../../lib/pharmacyFirst/utils/idGenerator";

const initialState = {
  activePlanId: null,
  plans: [],
  visits: [],
  doseAuditRecords: [],
  isLoading: false,
  activeSiteId: null,
  activeSiteName: "",
  isYellowFeverCentre: false,
  yellowFeverCentreNumber: "",
};

export const travelHealthSlice = createSlice({
  name: "travelHealth",
  initialState,
  reducers: {
    setActivePlan: (state, action) => {
      state.activePlanId = action.payload;
    },

    createPlan: {
      prepare(plan) {
        const id = generateId("TRAVELPLAN");
        return {
          payload: { ...plan, id, createdAt: new Date().toISOString() },
        };
      },
      reducer(state, action) {
        state.plans.push(action.payload);
        state.activePlanId = action.payload.id;
      },
    },

    updatePlanStatus: (state, action) => {
      const { planId, status } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (plan) plan.status = status;
    },

    addProductToBasket: {
      prepare(planId, product) {
        const id = generateId("TRAVELPRODUCT");
        return {
          payload: {
            planId,
            product: { ...product, id, addedAt: new Date().toISOString() },
          },
        };
      },
      reducer(state, action) {
        const { planId, product } = action.payload;
        const plan = state.plans.find((p) => p.id === planId);
        if (plan) plan.basket.push(product);
      },
    },

    updateProductStatus: (state, action) => {
      const { planId, productId, status } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (!plan) return;
      const product = plan.basket.find((p) => p.id === productId);
      if (product) product.status = status;
    },

    updateProductBrand: (state, action) => {
      const { planId, productId, brand } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (!plan) return;
      const product = plan.basket.find((p) => p.id === productId);
      if (product) product.selectedBrand = brand;
    },

    addDoseRecord: (state, action) => {
      const { planId, productId, doseEntry } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (!plan) return;
      const product = plan.basket.find((p) => p.id === productId);
      if (!product) return;
      const existingIndex = product.doseSchedule.findIndex(
        (entry) => entry.doseNumber === doseEntry.doseNumber,
      );
      if (existingIndex === -1) {
        product.doseSchedule.push(doseEntry);
      } else {
        product.doseSchedule[existingIndex] = doseEntry;
      }
      product.currentDoseNumber = doseEntry.doseNumber;
    },

    recordAuditLink: (state, action) => {
      const { planId, productId, auditRecordId, visitId } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (plan) {
        const product = plan.basket.find((p) => p.id === productId);
        if (product) product.auditRecordIds.push(auditRecordId);
      }
      const visit = state.visits.find((v) => v.id === visitId);
      if (visit) visit.auditRecordIds.push(auditRecordId);
    },

    createVisit: {
      prepare(visit) {
        const id = generateId("TRAVELVISIT");
        return {
          payload: { ...visit, id, createdAt: new Date().toISOString() },
        };
      },
      reducer(state, action) {
        state.visits.push(action.payload);
      },
    },

    recordDoseAuditDetail: (state, action) => {
      state.doseAuditRecords.push(action.payload);
    },

    setCoAdminMatrix: (state, action) => {
      const { planId, constraints } = action.payload;
      const plan = state.plans.find((p) => p.id === planId);
      if (plan) plan.coAdminMatrixOutput = constraints;
    },

    setActiveSite: (state, action) => {
      const { id, name, isYellowFeverCentre, yellowFeverCentreNumber } =
        action.payload;
      state.activeSiteId = id;
      state.activeSiteName = name;
      state.isYellowFeverCentre = isYellowFeverCentre;
      state.yellowFeverCentreNumber = yellowFeverCentreNumber;
    },

    clearActiveSite: (state) => {
      state.activeSiteId = null;
      state.activeSiteName = "";
      state.isYellowFeverCentre = false;
      state.yellowFeverCentreNumber = "";
    },

    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    resetTravelHealthState: () => initialState,
  },
});

export const {
  setActivePlan,
  createPlan,
  updatePlanStatus,
  addProductToBasket,
  updateProductStatus,
  updateProductBrand,
  addDoseRecord,
  recordAuditLink,
  createVisit,
  recordDoseAuditDetail,
  setCoAdminMatrix,
  setActiveSite,
  clearActiveSite,
  setLoading,
  resetTravelHealthState,
} = travelHealthSlice.actions;

export default travelHealthSlice.reducer;
